import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import axios from 'axios'
import './ProfilePage.css'

export default function ProfilePage({ user }) {
  const [profile, setProfile] = useState(null)
  const [friends, setFriends] = useState([])
  const [pendingInvites, setPendingInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [inviteName, setInviteName] = useState('')
  const [inviteAge, setInviteAge] = useState('')
  const [creatingInvite, setCreatingInvite] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      const [profileResponse, invitesResponse] = await Promise.all([
        axios.get('/api/users/profile', { withCredentials: true }),
        axios.get('/api/invites/pending', { withCredentials: true })
      ])

      setProfile(profileResponse.data.profile)
      setFriends(profileResponse.data.friends)
      setPendingInvites(invitesResponse.data)
    } catch (error) {
      console.error('Failed to load profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const createInvite = async (e) => {
    e.preventDefault()
    setCreatingInvite(true)

    try {
      const response = await axios.post(
        '/api/invites/create',
        { invited_name: inviteName, invited_age: parseInt(inviteAge) },
        { withCredentials: true }
      )

      alert(`Invite created! Share this link with your friend:\n\n${response.data.invite_url}\n\nYour parent needs to approve this invite first.`)
      setInviteName('')
      setInviteAge('')
      await loadProfile() // Reload to show new invite
    } catch (error) {
      console.error('Failed to create invite:', error)
      alert('Failed to create invite. Please try again.')
    } finally {
      setCreatingInvite(false)
    }
  }

  const resetXP = async () => {
    if (!window.confirm('Reset all XP and level back to 0? This cannot be undone! You will lose all progress.')) {
      return
    }

    try {
      await axios.post('/api/users/reset-xp', {}, { withCredentials: true })
      await loadProfile() // Reload to show reset stats
      alert('XP and level reset to 0!')
    } catch (error) {
      console.error('Failed to reset XP:', error)
      alert('Failed to reset XP. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="profile-loading">
            <div className="spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="page-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="container">
        <h1 className="page-title">Profile</h1>

        <div className="profile-header">
          <img
            src={profile.avatar_url || '/avatars/link.svg'}
            alt={profile.display_name}
            className="profile-avatar"
          />
          <div className="profile-info">
            <h2 className="profile-name">{profile.display_name}</h2>
            <div className="profile-level">
              Level {profile.current_level} Reviewer
            </div>
          </div>
        </div>

        <div className="profile-stats">
          <div className="stat-card">
            <div className="stat-value">{profile.review_count || 0}</div>
            <div className="stat-label">Reviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{profile.total_xp || 0}</div>
            <div className="stat-label">Total XP</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{profile.xp_to_next_level || 0}</div>
            <div className="stat-label">XP to Next Level</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{profile.friend_count || 0}</div>
            <div className="stat-label">Friends</div>
          </div>
        </div>

        <div className="profile-actions" style={{ marginTop: 'var(--spacing-lg)', textAlign: 'center' }}>
          <motion.button
            className="btn btn-danger"
            onClick={resetXP}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Reset all XP and level progress"
          >
            🔄 Reset XP
          </motion.button>
        </div>

        <div className="profile-section">
          <h3 className="section-title">Friends 👥</h3>
          {friends.length > 0 ? (
            <div className="friends-list">
              {friends.map((friend) => (
                <div key={friend.user_id} className="friend-item">
                  <img
                    src={friend.avatar_url || '/avatars/link.svg'}
                    alt={friend.display_name}
                    className="friend-avatar"
                  />
                  <div className="friend-info">
                    <div className="friend-name">{friend.display_name}</div>
                    <div className="friend-since">
                      Friends since {new Date(friend.friend_since).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-message">No friends yet. Invite someone below!</p>
          )}
        </div>

        <div className="profile-section">
          <h3 className="section-title">Invite a Friend ➕</h3>
          <form className="invite-form" onSubmit={createInvite}>
            <input
              type="text"
              className="input"
              placeholder="Friend's name"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              required
            />
            <input
              type="number"
              className="input"
              placeholder="Friend's age"
              value={inviteAge}
              onChange={(e) => setInviteAge(e.target.value)}
              min="5"
              max="15"
              required
            />
            <motion.button
              type="submit"
              className="btn btn-primary"
              disabled={creatingInvite}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {creatingInvite ? 'Creating...' : 'Create Invite'}
            </motion.button>
          </form>
        </div>

        {pendingInvites.length > 0 && (
          <div className="profile-section">
            <h3 className="section-title">Pending Invites ⏳</h3>
            <div className="invites-list">
              {pendingInvites.map((invite) => (
                <div key={invite.invite_id} className="invite-item">
                  <div className="invite-info">
                    <div className="invite-name">
                      {invite.invited_name} (age {invite.invited_age})
                    </div>
                    <div className="invite-status">
                      Status: {invite.status === 'pending' ? '⏳ Waiting for parent approval' : invite.status}
                    </div>
                    <div className="invite-code">
                      Code: {invite.invite_code}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )
}
